import { getFirstModuleOfArray } from "../routes/manageRouting";
import { createLink } from "colbi_web_ui/lib/utils";
import { UserRoles } from "../enums/userRoles";
import { jwtDecode } from "jwt-decode";

/**
 * @description receives a navigation array and returns all nested links in the navigation
 * @param navigation
 * @returns returns nestedLinks array
 **/
export function buildNestedNavigationLinks(
    navigation,
    history,
    params,
    rootLabel,
    i18n,
    addRootLabel,
    concatenatedMenuLabels,
) {
    const nestedLinks = [];

    const nestedLinksItem = {
        id: "",
        label: null,
        key: "data",
        to: "",
        action: () => {},
        icon: null,
    };

    for (let element of navigation) {
        const elementName = i18n(String(element.name).replace(/,/g, "").trim());
        if (element.navigation && element.navigation.length > 0) {
            buildNestedNavigationLinks(
                element.navigation,
                history,
                params,
                elementName,
                i18n,
                addRootLabel,
                rootLabel + "_" + elementName,
            ).forEach((nestedLink) => {
                nestedLinks.push(nestedLink);
            });
        } else {
            if (element.to) {
                let tmpName = elementName;

                if (addRootLabel) {
                    tmpName = `${rootLabel} ${elementName}`.replace(/\s?\(\d+\)/, "").trim();
                }

                const nestedItem = JSON.parse(JSON.stringify(nestedLinksItem));
                nestedItem.id = concatenatedMenuLabels + "_" + elementName;
                nestedItem.label = tmpName;
                nestedItem.key = "data";
                nestedItem.action = () => {};
                nestedItem.icon = element.icon;

                const url = hydrateRoute(element.to, params);
                nestedItem.to = url;
                nestedItem.action = () => {
                    history.push(url, { selectedLink: tmpName });
                };

                nestedLinks.push(nestedItem);
            }
        }
    }

    return nestedLinks;
}

/**
 * @description receives a navigation item, transform to a Static Link object and return it
 * @param item
 * @param history
 * @param translateIcons
 * @param params
 * @returns returns a static link object
 **/
export function transformNavigationItem2StaticLink(item, history, params, i18n) {
    const itemName = i18n(String(item.name).replace(/,/g, "").trim());
    const outputItem = {
        id: itemName,
        label: itemName || "",
        key: "data",
        to: "",
        action: () => {},
        icon: item.icon,
    };

    if (item.to) {
        const url = hydrateRoute(item.to, params);
        outputItem.to = url;
        outputItem.action = () => {
            history.push(url, { selectedLink: itemName });
        };
    }

    return outputItem;
}

export const buildStaticLinks = (navigation, history, matchParams, i18n) => {
    return (navigation || []).map((item) => {
        let outputItem = transformNavigationItem2StaticLink(item, history, matchParams, i18n);
        if (item.navigation) {
            const nestedLinks = buildNestedNavigationLinks(
                item.navigation,
                history,
                matchParams,
                outputItem.id,
                i18n,
                false,
                outputItem.id,
            );

            if (nestedLinks.length > 0) {
                delete outputItem.action;
                outputItem.nestedLinks = nestedLinks;

                return outputItem;
            }
        }

        return outputItem;
    });
};

export function hydrateRoute(routePath, params) {
    const withoutLocale = (routePath || "").replace(/^\/:locale\(.+?\)\?/, "");
    const hydrated = withoutLocale.replace(/:[a-zA-Z]+(\(.+?\))?\??\*?/gi, (part) => {
        const paramName = part.slice(1).replace(/\(.*?\)\??\*?|\?|\*/, "");
        const defaultValue = part.match(/\?$/) ? "" : part.match(/^:/) ? "" : part;
        return params[paramName] !== undefined ? params[paramName] : defaultValue;
    });
    return (
        params.locale && hydrated.match(/^\//) ? `/${params.locale}${hydrated}` : hydrated
    ).replace(/\/+/gi, "/");
}

export const setAccount = (account, locale, history, proxy, params) => {
    if (account && account.id) {
        if (account.typeEntity === "org") {
            history.push(`/${locale}/organizations/${account["id"]}`);
        } else {
            setProjectAccount(account, history, proxy, params);
        }
    }
};

export const setProjectAccount = (project, history, proxy, params) => {
    //TODO: Clean this, copy from mapProjectsByOrganization
    let link;
    if (project && project.fiscalYears && Object.keys(project.fiscalYears).length > 0) {
        const mostRecentFiscalYear = Math.max(...Object.keys(project.fiscalYears));
        const projectModules = project && project.modules;
        const findFirstModule = getFirstModuleOfArray(
            (projectModules || []).filter((r) => r.name !== "repository").map((elem) => elem.name),
        );
        const checkForOnlyRepo = (projectModules || []).some((elem) => elem.name === "archive");
        const moduleFound = (projectModules || []).find((elem) => elem.name === findFirstModule);
        //found an matched modules and not default (repository)
        let typeOfNavigation = "full";
        let moduleOrRouteToUse = "";
        if (moduleFound && !checkForOnlyRepo) {
            moduleOrRouteToUse = moduleFound.name;
            if (moduleFound.navigation_by_import_id && moduleFound.navigation_by_import_id !== 2) {
                const filterFullImportId = project.fiscalYears[mostRecentFiscalYear]
                    .filter(({ id }) => id !== "full")
                    .map(({ id }) => id);
                if (filterFullImportId && filterFullImportId.length) {
                    typeOfNavigation = Math.max(...filterFullImportId);
                }
            }
            if (moduleFound.default_route && moduleFound.default_route.length) {
                moduleOrRouteToUse = moduleFound.default_route;
            }
            link = createLink(
                null,
                proxy,
                {
                    ...project,
                    to: `/${project.id}/${mostRecentFiscalYear}/${typeOfNavigation}/${moduleOrRouteToUse}`,
                },
                params,
            );
        }
    }

    if (!link) {
        //if no data imported navigate to repository
        link = createLink(
            null,
            proxy,
            { ...project, to: `/${project.id}/repository/activity/status` },
            params,
        );
    }

    history.push(link);
};

const settingsMenu = (role, history, locale, i18n) => {
    const settingsRoles = [
        UserRoles.ADMIN,
        UserRoles.BACKOFFICE,
        UserRoles.PP_SUPPORT,
        UserRoles.SUPPORT,
    ];
    const menu = [];
    if (settingsRoles.includes(role)) {
        menu.push({
            label: i18n`organizations`,
            action: () => {
                history.push(`/${locale}/settings/organizations`, {
                    selectedLink: i18n`organizations`,
                });
            },
        });
    }

    menu.push(
        {
            label: i18n`projects`,
            action: () => {
                history.push(`/${locale}/settings/projects`, { selectedLink: i18n`projects` });
            },
        },
        {
            label: i18n`users`,
            action: () => {
                history.push(`/${locale}/settings/users`, { selectedLink: i18n`users` });
            },
        },
    );

    if (settingsRoles.includes(role)) {
        menu.push(
            {
                label: i18n`rules`,
                action: () => {
                    history.push(`/${locale}/settings/nc_rules`, { selectedLink: i18n`rules` });
                },
            },
            {
                label: i18n`settings_menu_kpis`,
                action: () => {
                    history.push(`/${locale}/settings/kpis`, {
                        selectedLink: i18n`kpis`,
                    });
                },
            },
            {
                label: i18n`custom_reports`,
                action: () => {
                    history.push(`/${locale}/settings/custom_reports`, {
                        selectedLink: i18n`custom_reports`,
                    });
                },
            },
            {
                label: i18n`packages`,
                action: () => {
                    history.push(`/${locale}/settings/packages`, {
                        selectedLink: i18n`packages`,
                    });
                },
            },
        );
    }
    return menu;
};

export const footerProps = (history, locale, user, setIsLanguageModalOpen, logout, i18n) => {
    return {
        labels: {
            collapseIcon: i18n`collapse`,
            userIcon: i18n`your account`,
            settingsIcon: i18n`settings`,
        },
        showSettingsButton: user.accessSettings,
        settingsMenuItems: settingsMenu(user.role, history, locale, i18n),
        userName: user.name,
        userMenuItems: [
            {
                label: i18n`language`,
                action: () => setIsLanguageModalOpen(true),
            },
            {
                label: i18n`change password`,
                action: () => {
                    history.push(`/${locale}/my_profile/reset_password`);
                },
            },
            {
                label: i18n`logout`,
                action: () => {
                    logout();
                },
            },
        ],
    };
};

export function parseJson(json) {
    try {
        return JSON.parse(json);
    } catch (e) {
        return json;
    }
}

export const metadataStringify = (colomunsConfiguration, availableConditionsFiels) => {
    return JSON.stringify({
        columnsConfiguration: colomunsConfiguration,
        availableConditionsFields: availableConditionsFiels,
    });
};

export const checkIfProjectHasFrModule = (currentArrayModules) => {
    if (currentArrayModules.includes("SAFTFRFILE")) {
        return true;
    }
    return false;
};

export const removeKeyRecursively = (metadata, keyToRemove) => {
    const parsedMetadata = parseJson(metadata);
    const tempMetadata = { ...parsedMetadata };
    for (let prop in tempMetadata) {
        if (typeof tempMetadata[prop] === "object") {
            removeKeyRecursively(tempMetadata[prop], keyToRemove);
        } else if (prop === keyToRemove) {
            delete tempMetadata[prop];
        }
    }
    return JSON.stringify(tempMetadata, undefined, 4);
};

export const findFileVersionValue = (metadata, keyToFind) => {
    const parsedMetadata = parseJson(metadata);
    for (let prop in parsedMetadata) {
        if (typeof parsedMetadata[prop] === "object") {
            let result = findFileVersionValue(parsedMetadata[prop], keyToFind);
            if (result !== undefined) {
                return result;
            }
        } else if (prop === keyToFind) {
            return parsedMetadata[prop];
        }
    }
};

export const decodeUserSession = (user) => {
    try {
        const decoded = jwtDecode(user.session);
        return decoded.user;
    } catch (e) {
        return null;
    }
};
